// @ts-strict-ignore
import React, {useEffect, useState} from 'react';
import copy from 'clipboard-copy';
import take from 'lodash/take';
import {Typography} from 'onboarding-components';

import {TooltipReact} from 'common/components/tooltip';
import {Placement, TooltipTextAlign} from 'common/components/tooltip/types';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {useCurrentUser} from 'common/contexts/react/current-user-context';
import {IMMERSIVE_ASSETS} from 'common/onboarding/immersive-onboarding/assets';
import {reporter} from 'common/report/error-reporting';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {useTranslation} from 'common/i18n/hooks';
import {TrackingEvent, trackOnboardingEvent} from 'common/onboarding/immersive-onboarding/tracking';
import {DuplicateIcon} from 'iron/common/onboarding/immersive-onboarding/assets/duplicate-icon';
import {SetupGuideContext} from 'common/onboarding/immersive-onboarding/types';

import styles from './project-token.module.styl';

// Define the maximum number of owners to display in the tooltip
const MAX_OWNER_DISPLAY_COUNT = 4;

export const ProjectToken = () => {
  const {t} = useTranslation();
  const {setupGuideInfo} = React.useContext(SetupGuideContext);
  const currentUser = useCurrentUser();
  const currentProject = useCurrentProject();
  const [isCopying, setIsCopying] = useState<boolean>(false);
  const [owners, setOwners] = useState([]);

  // Fetch owners account data when component mounts
  useEffect(() => {
    const getOwnersAccount = async () => {
      try {
        const {results} = await fetch(`/organizations/${currentProject.get()?.value.organizationId}/users`, {
          method: `GET`,
        }).then((response) => response.json());

        setOwners(
          results
            .filter((user) => user.org_role === `owner` || user.org_role === `admin`)
            .sort((a, b) => b.last_activity - a.last_activity),
        );
      } catch {
        throw new Error(`Error retrieving users`);
      }
    };

    getOwnersAccount();
  }, [currentProject]);

  // Copy token functionality
  const copyToken = async (token: string) => {
    try {
      await copy(token);
      setIsCopying(true);
      setTimeout(() => {
        setIsCopying(false);
      }, 1500);
      trackOnboardingEvent(TrackingEvent.TokenCopied);
    } catch (err) {
      reporter.error(err);
      addToast({
        type: ToastType.ERROR,
        message: t(`onboarding.implement.copy-token-error`),
      });
    }
  };

  // Helper to format owners for tooltip
  const getOwnersString = () => {
    let ownersEmail = take(owners, MAX_OWNER_DISPLAY_COUNT).reduce((acc, user) => (acc += `${user.email}\n`), ``);
    const numRemainingOwners = owners.slice(MAX_OWNER_DISPLAY_COUNT).length;
    ownersEmail +=
      numRemainingOwners > 0
        ? t(`onboarding.implement.remaining-owners`, {remainingOwners: owners.slice(MAX_OWNER_DISPLAY_COUNT).length})
        : ``;
    return ownersEmail;
  };

  // Helper to get organization name
  const getOrgName = () => {
    const orgId = currentProject.get()?.value.organizationId;
    return currentUser.get()?.value?.organizations[orgId]?.name;
  };

  // Determine what text to display in the token field
  let tokenText: string;

  if (isCopying) {
    tokenText = t(`onboarding.implement.copied`);
  } else if (setupGuideInfo?.projectToken) {
    tokenText = setupGuideInfo?.projectToken;
  }

  return (
    <div className={styles.tokenContainer}>
      <div className={styles.tokenHeader}>
        <Typography variant='captionXs'>{t(`onboarding.implement.copy-token`)}</Typography>
        <div className={styles.infoTooltip}>
          <img src={IMMERSIVE_ASSETS.help} className={styles.infoIcon} />
          <TooltipReact placement={Placement.Top} text={t(`onboarding.implement.token-tooltip`)} maxWidth={`200px`} />
        </div>
      </div>

      <button
        className={styles.copyTokenButton}
        onClick={() => !isCopying && setupGuideInfo?.projectToken && copyToken(setupGuideInfo?.projectToken)}
        disabled={!tokenText}
      >
        {/* hide the full token in code to prevent unauth access. text-overflow will ellipsify */}
        <div className={styles.tokenText}>{tokenText ? tokenText.slice(0, 12) : `*********`}</div>

        <DuplicateIcon className={styles.duplicateIcon} />

        {!tokenText && (
          <TooltipReact
            placement={Placement.Bottom}
            text={t(`onboarding.implement.no-permission-tooltip`, {
              orgName: getOrgName(),
              ownersString: getOwnersString(),
            })}
            textAlign={TooltipTextAlign.Start}
            maxWidth={`250px`}
          />
        )}
      </button>
    </div>
  );
};
